/**
 * Everything one visit did, on one screen.
 *
 * The trail lived inside the pipeline row, which was fine at nine events and
 * unreadable at two hundred: the row grew taller than the window and the
 * board underneath it disappeared. In the row it is now one line saying how
 * many there were; this is where they are actually read.
 *
 * Time-ordered, never grouped. A journey is a sequence — they landed, they
 * scrolled to pricing, they picked a plan, they hit a validation error, they
 * left — and the moment it is bucketed by kind that sentence is gone.
 *
 * Every row is a link. The whole point of looking at a click is to go and
 * see the thing that was clicked, and a click carries its own href where the
 * tracker caught one, so "Book a demo" opens the demo page rather than the
 * page it was clicked from.
 */
import { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api.js';
import { isOffline, parseIntel } from '../../lib/storage.js';
import Icon from '../../lib/icons.jsx';
import { spent } from '../../lib/format.js';
import { parseDate } from '../../lib/dates.js';
import Sheet from '../Sheet.jsx';

/** "/payment/signup" — the path, without the tracking that follows it. */
const pathOf = url => {
  const p = String(url || '').replace(/^https?:\/\/[^/]+/, '').split(/[?#]/)[0];
  return p || '/';
};

/** "thexyra.ai" — only shown when a click leaves the site. */
const hostOf = url => (String(url || '').match(/^https?:\/\/([^/]+)/) || [])[1] || '';

/*
 * The four things the tracker records, each with its own colour.
 *
 * Colour is doing real work here rather than decorating: at two hundred rows
 * the eye finds "where did they click" by hue long before it reads a word.
 * Anything the tracker sends that is not one of these reads as a click,
 * which is what every unknown interaction has turned out to be.
 */
const KINDS = {
  pageview: { label: 'Page', icon: 'file' },
  click: { label: 'Click', icon: 'target' },
  scroll: { label: 'Scroll', icon: 'down' },
  exit: { label: 'Exit', icon: 'out' }
};
const ORDER = ['pageview', 'click', 'scroll', 'exit'];
const kindOf = e => (KINDS[e.type] ? e.type : 'click');

/** "2:41:07 pm". The date is in the header; repeating it every row is noise. */
const clock = at => {
  const d = parseDate(at);
  if (!d) return '';
  return d
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' })
    .toLowerCase();
};

/** What a row says it is, in one line, so search can run against it. */
const textOf = e => {
  const el = e.el || {};
  return [
    KINDS[kindOf(e)].label, e.title, el.text, el.selector, el.href, e.url,
    e.pct == null ? '' : `${e.pct}%`
  ].filter(Boolean).join(' ').toLowerCase();
};

/** One event. */
function Row({ e, n }) {
  const kind = kindOf(e);
  const el = e.el || {};
  /* A click knows where it was going; everything else only knows where it
     happened. Either way the row goes somewhere. */
  const href = el.href || e.url || '';
  const away = !!hostOf(href) && hostOf(href) !== hostOf(e.url);

  const what = kind === 'scroll'
    ? `${e.pct ?? 0}% down ${e.title || pathOf(e.url)}`
    : kind === 'exit'
      ? `Left ${e.title || pathOf(e.url)}`
      : (el.text || e.title || pathOf(e.url));

  const Tag = href ? 'a' : 'div';
  const link = href ? { href, target: '_blank', rel: 'noreferrer' } : {};

  return (
    <li className={`pj-row ${kind}`}>
      <span className="pj-time">{clock(e.at)}</span>
      <span className="pj-rail" aria-hidden="true"><i /></span>

      <Tag className="pj-body" {...link} title={href || undefined}>
        <span className="pj-kind">
          <Icon name={KINDS[kind].icon} />{KINDS[kind].label}
        </span>
        <span className="pj-what">{what}</span>

        {kind === 'scroll' ? (
          <span className="pj-bar" aria-hidden="true">
            <i style={{ width: `${Math.max(3, Math.min(100, e.pct || 0))}%` }} />
          </span>
        ) : null}

        <span className="pj-where">
          {pathOf(href || e.url)}
          {away ? <b> · {hostOf(href)}</b> : null}
        </span>

        {el.selector && kind === 'click'
          ? <code className="pj-sel">{el.selector}</code> : null}

        {e.seconds ? <em className="pj-secs">{spent(e.seconds)}</em> : null}
        {href ? <Icon name="link" /> : null}
      </Tag>

      <span className="pj-n" aria-hidden="true">{n}</span>
    </li>
  );
}

export default function PageJourneySheet({ visit = {}, dealId, index = 0, who, at, onClose }) {
  const [q, setQ] = useState('');
  const [kinds, setKinds] = useState([]);

  /*
   * The trail is fetched, not carried.
   *
   * A board of fourteen hundred rows was downloading thirty thousand click
   * records to draw none of them — 70% of the whole response — so the list
   * now sends the counts and leaves the events behind. They are read for the
   * one visit somebody actually opened, which is this window.
   */
  const [fetched, setFetched] = useState(null);
  const [busy, setBusy] = useState(false);

  const carried = Array.isArray(visit.events) ? visit.events : null;

  useEffect(() => {
    if (carried || !dealId || isOffline()) return undefined;
    let gone = false;
    setBusy(true);
    api.deals.get(dealId)
      .then(row => {
        if (gone) return;
        const intel = parseIntel(row && (row.lead_intel ?? row.intel));
        const v = ((intel && intel.sessions) || [])[index];
        setFetched((v && v.events) || []);
      })
      .catch(() => { if (!gone) setFetched([]); })
      .finally(() => { if (!gone) setBusy(false); });
    return () => { gone = true; };
  }, [carried, dealId, index]);

  const all = carried || fetched || [];

  /* Counts come off the whole visit, not off what the search has left: a
     chip that changes its own number as you type is a chip nobody trusts. */
  const counts = useMemo(() => {
    const n = {};
    all.forEach(e => { const k = kindOf(e); n[k] = (n[k] || 0) + 1; });
    return n;
  }, [all]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all
      .map((e, i) => [e, i])
      .filter(([e]) => {
        if (kinds.length && !kinds.includes(kindOf(e))) return false;
        return !needle || textOf(e).includes(needle);
      });
  }, [all, q, kinds]);

  const toggle = k =>
    setKinds(prev => (prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]));

  const seconds = all.reduce((t, e) => t + (Number(e.seconds) || 0), 0);
  /* The counts the list already sent, until the trail itself arrives — so the
     header reads '8 pages' while it loads rather than '0 pages'. */
  const stats = visit.eventStats || {};
  const pages = all.length ? all.filter(e => e.type === 'pageview').length : (stats.pageview || 0);

  return (
    <Sheet
      icon="flow"
      tone="g-blue"
      wide
      title="Page journey"
      badge={<span className="pj-badge">{(all.length || (visit.eventStats || {}).total || 0)} events</span>}
      onClose={onClose}
      closeLabel="Done"
      bodyClass="pj-form"
      hero={(
        <div className="pj-hero">
          <div className="pj-who">
            <b>{who || 'This visitor'}</b>
            <span>
              {at ? `${at} · ` : ''}
              {pages} page{pages === 1 ? '' : 's'}
              {seconds ? ` · ${spent(seconds)} on site` : ''}
            </span>
          </div>

          <div className="pj-tools">
            <div className="pj-search">
              <Icon name="search" />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search a page, a button, a selector…"
                aria-label="Search this journey"
              />
              {q ? (
                <button
                  type="button"
                  className="iconbtn"
                  aria-label="Clear the search"
                  onClick={() => setQ('')}
                >
                  <Icon name="x" />
                </button>
              ) : null}
            </div>

            <div className="pj-chips">
              {ORDER.filter(k => counts[k]).map(k => (
                <button
                  key={k}
                  type="button"
                  className={`pj-chip ${k}${kinds.includes(k) ? ' on' : ''}`}
                  onClick={() => toggle(k)}
                >
                  <Icon name={KINDS[k].icon} />{KINDS[k].label}
                  <b>{counts[k]}</b>
                </button>
              ))}
              {kinds.length ? (
                <button type="button" className="pj-chip clear" onClick={() => setKinds([])}>
                  Clear
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}
    >
      {shown.length ? (
        <ol className="pj-list">
          {shown.map(([e, i]) => <Row key={`${e.at}-${i}`} e={e} n={i + 1} />)}
        </ol>
      ) : (
        <p className="pj-none">
          {busy
            ? 'Reading the trail…'
            : all.length
              ? 'Nothing here matches that.'
              : 'The tracker recorded no events for this visit.'}
        </p>
      )}
    </Sheet>
  );
}
